import PropTypes from 'prop-types';

import {RegistrySupportedType} from 'constants/RegistryConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

export const MAX_SCHEMA_FIELD_DEPTH = 10;

export interface LookupConfigModalContextInfo {
  sourceLookupInfos?: Array<LookupStepInfo> | null;
  destinationLookupInfos?: Array<LookupStepInfo> | null;
  usePlateLayout?: boolean;
  processTaskSchema?: Schema;
  flowchartTaskSchema?: Schema;
  // Frontend only additional metadata to be smarter about what following lookups to show when we don't
  // know what specific type a storable lookup is, but know it must be a parent storable
  storableLookupMustBeParent?: boolean;
}

// Keep in sync with LookupStepType in benchling/lookup_steps/enums.py
export enum LookupStepType {
  AA_PART_LINKS_TO_ENTITY = 'AA_PART_LINKS_TO_ENTITY',
  ALIASES = 'ALIASES',
  AUTHORIZED_USERS = 'AUTHORIZED_USERS',
  AVAILABLE_SPACE = 'AVAILABLE_SPACE',
  BARCODE = 'BARCODE',
  BOX = 'BOX',
  BOX_CONTAINERS = 'BOX_CONTAINERS',
  CHEMICAL_STRUCTURE = 'CHEMICAL_STRUCTURE',
  CHILD_ENTITIES = 'CHILD_ENTITIES',
  COMPUTED_EXPIRATION_DATE = 'COMPUTED_EXPIRATION_DATE',
  CONCENTRATION = 'CONCENTRATION',
  CONSTANT = 'CONSTANT',
  CONTAINER = 'CONTAINER',
  CONTAINER_BY_GRID_POSITION = 'CONTAINER_BY_GRID_POSITION',
  CONTENTS = 'CONTENTS',
  COUNT = 'COUNT',
  DESTINATION = 'DESTINATION',
  FILTER = 'FILTER',
  FILTER_BY_ORIGIN = 'FILTER_BY_ORIGIN',
  FLOWCHART_PROCESS_TASK = 'FLOWCHART_PROCESS_TASK',
  FREEZE_THAW_COUNT = 'FREEZE_THAW_COUNT',
  LATEST = 'LATEST',
  LOCATION = 'LOCATION',
  LOCATION_PATH = 'LOCATION_PATH',
  NAME = 'NAME',
  NOTEBOOK_COLUMN_TYPECHECK = 'NOTEBOOK_COLUMN_TYPECHECK',
  PART_LINKS_TO_ENTITY = 'PART_LINKS_TO_ENTITY',
  PLACEHOLDER_BOXES = 'PLACEHOLDER_BOXES',
  PLACEHOLDER_PLATES = 'PLACEHOLDER_PLATES',
  PLATE = 'PLATE',
  PROCESS_TASK = 'PROCESS_TASK',
  PROCESS_TASK_DEFINITION_FIELD = 'PROCESS_TASK_DEFINITION_FIELD',
  QUANTITY = 'VOLUME',
  REGISTRY_IDENTIFIER = 'REGISTRY_ID',
  REPLICATES = 'REPLICATES',
  RESIDUES = 'RESIDUES',
  RESULTS = 'RESULTS',
  SAMPLE_OWNERS = 'SAMPLE_OWNERS',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SCHEMA_IDENTITY = 'SCHEMA_IDENTITY',
  SOURCE = 'SOURCE',
  STUDIES = 'STUDIES',
  TASK_FINDER_PROCESS_TASKS = 'PROCESS_TASKS',
  TRANSLATION_LINKS_TO_ENTITY = 'TRANSLATION_LINKS_TO_ENTITY',
  WELL_COORDINATES = 'WELL_COORDINATES',
  WELLS = 'WELLS',
}

// Keep these in sync with MODIFIER_LOOKUP_STEP_TYPES in
// benchling/lookup_steps/spec/lookup_step_list_spec_editor.py
export const MODIFIER_LOOKUP_STEP_TYPES = [
  LookupStepType.FILTER,
  LookupStepType.FILTER_BY_ORIGIN,
  LookupStepType.LATEST,
  LookupStepType.REPLICATES,
];
export type ModifierLookupStepType =
  | LookupStepType.FILTER
  | LookupStepType.FILTER_BY_ORIGIN
  | LookupStepType.LATEST
  | LookupStepType.REPLICATES;

// Keep in sync with COLLAPSING_LOOKUP_STEP_TYPES in benchling/lookup_steps/spec/lookup_step_list_spec_editor.py
export const COLLAPSING_LOOKUP_STEP_TYPES: Array<LookupStepType> = [LookupStepType.LATEST];

// Keep in sync with NotebookColumnItemType in benchling/lookup_steps/enums.py
export enum NotebookColumnItemType {
  CONTAINER = 'CONTAINER',
  ENTITY = 'ENTITY',
  PLATE = 'PLATE',
  BOX = 'BOX',
  LOCATION = 'LOCATION',
  STORABLE = 'STORABLE',
}

// Keep in sync with ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES in benchling/lookup_steps/enums.py
export const ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES = [
  NotebookColumnItemType.BOX,
  NotebookColumnItemType.CONTAINER,
  NotebookColumnItemType.LOCATION,
  NotebookColumnItemType.PLATE,
  NotebookColumnItemType.STORABLE,
];

// Keep in sync with LookupSortType in benchling/lookup_steps/enums.py
export enum LookupSortType {
  ORIGINAL = 'ORIGINAL',
  ALPHABETICAL = 'ALPHABETICAL',
}

// Keep in sync with benchling/lab_automation/filter.py:FilterOperatorEnum
export enum FilterType {
  LT = 'lt',
  LE = 'le',
  GE = 'ge',
  GT = 'gt',
  EQ = 'eq',
  NE = 'ne',
  ISIN = 'isin',
  NOTIN = 'notin',
  ISNULL = 'isnull',
  NOTNULL = 'notnull',
}

export const FILTER_TYPE_TO_READABLE: {[key in FilterType]: string} = {
  [FilterType.EQ]: 'is',
  [FilterType.NE]: 'is not',
  [FilterType.LT]: 'is less than',
  [FilterType.LE]: 'is at most',
  [FilterType.GE]: 'is at least',
  [FilterType.GT]: 'is greater than',
  [FilterType.ISNULL]: 'is empty',
  [FilterType.NOTNULL]: 'is not empty',
  [FilterType.ISIN]: 'includes',
  [FilterType.NOTIN]: 'does not include',
};

export const NUMERIC_FILTER_TYPES = [FilterType.LT, FilterType.LE, FilterType.GE, FilterType.GT];

export interface AaPartLinksToEntityLookupStepSpec {
  type: LookupStepType.AA_PART_LINKS_TO_ENTITY;
  entitySchema?: string; // Schema of containing AA sequences
}

export interface AliasesLookupStepSpec {
  type: LookupStepType.ALIASES;
}

export interface NameLookupStepSpec {
  type: LookupStepType.NAME;
}

export interface RestrictedSampleUsersLookupStepSpec {
  type: LookupStepType.AUTHORIZED_USERS;
}

export interface BoxLookupStepSpec {
  type: LookupStepType.BOX;
  boxSchema?: string;
}

export interface ChildEntitiesLookupStepSpec {
  type: LookupStepType.CHILD_ENTITIES;
  entitySchema?: string; // This is the child entity schema id
}

export enum FillDirection {
  ACROSS_ROWS = 'ACROSS_ROWS',
  DOWN_COLUMNS = 'DOWN_COLUMNS',
}

export interface GridOrderParams {
  fillDirection: FillDirection;
  skipRows: number;
  skipColumns: number;
  fillByQuadrant: boolean;
}

export interface GridFilterParams {
  ignoreEmpty: boolean;
  ignoreFilled: boolean;
  rowsToIgnore: Array<number | string>;
  columnsToIgnore: Array<number | string>;
  wellsToIgnore: Array<string>;
}

export interface AvailableSpaceLookupStepSpec {
  type: LookupStepType.AVAILABLE_SPACE;
}

export interface BarcodeLookupStepSpec {
  type: LookupStepType.BARCODE;
}

export interface BaseGridLookupStepSpec {
  order: GridOrderParams;
  filter: GridFilterParams;
}

export interface BoxContainersLookupStepSpec extends BaseGridLookupStepSpec {
  type: LookupStepType.BOX_CONTAINERS;
}

interface ChemicalStructureLookupStepSpec {
  type: LookupStepType.CHEMICAL_STRUCTURE;
}

export interface ComputedExpirationDateLookupStepSpec {
  type: LookupStepType.COMPUTED_EXPIRATION_DATE;
}

export interface ConcentrationLookupStepSpec {
  type: LookupStepType.CONCENTRATION;
  concentrationUnits: string;
}

export interface ConstantLookupStepSpec {
  type: LookupStepType.CONSTANT;
  value: string | number;
}

export interface ContainerLookupStepSpec {
  type: LookupStepType.CONTAINER;
  containerSchema?: string;
}

export interface ContentsLookupStepSpec {
  type: LookupStepType.CONTENTS;
  entitySchema?: string; // This is the target entity schema id
  entitySchemaType?: RegistrySupportedType;
}

export interface CountLookupStepSpec {
  type: LookupStepType.COUNT;
  // may be null if field has been deleted, but cannot be saved
  schemaField: string | null;
}

export interface DestinationLookupStepSpec {
  type: LookupStepType.DESTINATION;
}

export interface FilterLookupStepSpec {
  type: LookupStepType.FILTER;
  // may be null if field has been deleted, but cannot be saved
  schemaField: string | null;
  filterType: FilterType;
  value?: string | number;
}

export interface FreezeThawCountLookupStepSpec {
  type: LookupStepType.FREEZE_THAW_COUNT;
}

export interface LatestLookupStepSpec {
  type: LookupStepType.LATEST;
}

export interface LocationLookupStepSpec {
  type: LookupStepType.LOCATION;
  locationSchema?: string;
}

export interface LocationPathLookupStepSpec {
  type: LookupStepType.LOCATION_PATH;
}

export interface NotebookColumnTypecheckLookupStepSpec {
  type: LookupStepType.NOTEBOOK_COLUMN_TYPECHECK;
  notebookColumnItemType: NotebookColumnItemType;
  notebookColumnEntitySchema?: string;
  notebookColumnStorableSchema?: string;
}

export interface PartLinksToEntityLookupStepSpec {
  type: LookupStepType.PART_LINKS_TO_ENTITY;
  entitySchema?: string; // Schema of containing sequences
}

export interface PlaceholderBoxesLookupStepSpec {
  type: LookupStepType.PLACEHOLDER_BOXES;
  boxSchema: string;
}

export interface PlaceholderPlatesLookupStepSpec {
  type: LookupStepType.PLACEHOLDER_PLATES;
  plateSchema: string;
}

export interface PlateLookupStepSpec {
  type: LookupStepType.PLATE;
  plateSchema?: string;
}

export interface ProcessTaskLookupStepSpec {
  type: LookupStepType.PROCESS_TASK;
}

export interface ProcessTaskDefinitionFieldLookupStepSpec {
  type: LookupStepType.PROCESS_TASK_DEFINITION_FIELD;
  processTaskDefinitionVersion: string;
  processTaskDefinitionFieldIdentifier: string;
}

export interface FlowchartProcessTaskLookupStepSpec {
  type: LookupStepType.FLOWCHART_PROCESS_TASK;
  processTaskSchema: string;
}

export interface TaskFinderProcessTasksLookupStepSpec {
  type: LookupStepType.TASK_FINDER_PROCESS_TASKS;
  // may be null if field has been deleted, but cannot be saved
  linkedBySchemaField: string | null;
}

export interface RegistryIdentifierLookupStepSpec {
  type: LookupStepType.REGISTRY_IDENTIFIER;
}

export interface ReplicatesLookupStepSpec {
  type: LookupStepType.REPLICATES;
  numberLookupConfig: LookupConfigSpec;
}

interface ResiduesLookupStepSpec {
  type: LookupStepType.RESIDUES;
}

export interface ResultsLookupStepSpec {
  type: LookupStepType.RESULTS;
  // may be null if field has been deleted, but cannot be saved
  linkedBySchemaField: string | null;
}

export interface SampleOwnersLookupStepSpec {
  type: LookupStepType.SAMPLE_OWNERS;
}

export interface TypecastableLookupStepSpec {
  // Can be present if the schemaField is a link to an interface, and the user desires to typecast to an implementing schema of the interface
  typecastSchema?: string | null;
}

export interface SchemaFieldLookupStepSpec extends TypecastableLookupStepSpec {
  type: LookupStepType.SCHEMA_FIELD;
  // may be null if field has been deleted, but cannot be saved
  schemaField: string | null;
  typecastSchema?: string | null;
}

export interface SchemaIdentityLookupStepSpec {
  type: LookupStepType.SCHEMA_IDENTITY;
  entitySchema: string; // this is the entity schema ID
}

export interface SourceLookupStepSpec {
  type: LookupStepType.SOURCE;
}

export interface StudiesLookupStepSpec {
  type: LookupStepType.STUDIES;
}

export interface TranslationLinksToEntityLookupStepSpec {
  type: LookupStepType.TRANSLATION_LINKS_TO_ENTITY;
  entitySchema?: string; // Schema of DNA sequences
}

export interface QuantityLookupStepSpec {
  type: LookupStepType.QUANTITY;
  volumeUnits: string;
}

export enum CoordinateType {
  LETTER_AND_NUMBER = 'LETTER_AND_NUMBER',
  LETTER_AND_TWO_DIGIT_NUMBER = 'LETTER_AND_TWO_DIGIT_NUMBER',
  LETTER_ONLY = 'LETTER_ONLY',
  NUMBER_ONLY = 'NUMBER_ONLY',
}

export interface WellCoordinatesLookupStepSpec {
  type: LookupStepType.WELL_COORDINATES;
  coordinatesFillDirection?: FillDirection | null;
  coordinatesType?: CoordinateType | null;
}

export interface WellsLookupStepSpec extends BaseGridLookupStepSpec {
  type: LookupStepType.WELLS;
}

export interface ContainerByGridPositionLookupStepSpec {
  type: LookupStepType.CONTAINER_BY_GRID_POSITION;
  // If either field is null, lookup will just return null
  // may be null if field has been deleted, but cannot be saved
  schemaField: string | null; // plate/box (storable link field)
  // may be null if field has been deleted, but cannot be saved
  gridPositionSchemaField: string | null; // grid position (text field)
}

export interface FilterByOriginLookupStepSpec {
  type: LookupStepType.FILTER_BY_ORIGIN;
}

// Keep in sync with LookupStepSpecEditor in benchling/lookup_steps/spec/lookup_step_spec_editor.py
export type LookupStepSpec =
  | AaPartLinksToEntityLookupStepSpec
  | AliasesLookupStepSpec
  | AvailableSpaceLookupStepSpec
  | BarcodeLookupStepSpec
  | BoxContainersLookupStepSpec
  | BoxLookupStepSpec
  | ChemicalStructureLookupStepSpec
  | ChildEntitiesLookupStepSpec
  | ComputedExpirationDateLookupStepSpec
  | ConcentrationLookupStepSpec
  | ConstantLookupStepSpec
  | ContainerByGridPositionLookupStepSpec
  | ContainerLookupStepSpec
  | ContentsLookupStepSpec
  | CountLookupStepSpec
  | DestinationLookupStepSpec
  | FilterByOriginLookupStepSpec
  | FilterLookupStepSpec
  | FlowchartProcessTaskLookupStepSpec
  | FreezeThawCountLookupStepSpec
  | LatestLookupStepSpec
  | LocationLookupStepSpec
  | LocationPathLookupStepSpec
  | NameLookupStepSpec
  | NotebookColumnTypecheckLookupStepSpec
  | PartLinksToEntityLookupStepSpec
  | PlaceholderBoxesLookupStepSpec
  | PlaceholderPlatesLookupStepSpec
  | PlateLookupStepSpec
  | ProcessTaskDefinitionFieldLookupStepSpec
  | ProcessTaskLookupStepSpec
  | QuantityLookupStepSpec
  | RegistryIdentifierLookupStepSpec
  | ReplicatesLookupStepSpec
  | ResiduesLookupStepSpec
  | RestrictedSampleUsersLookupStepSpec
  | ResultsLookupStepSpec
  | SampleOwnersLookupStepSpec
  | SchemaFieldLookupStepSpec
  | SchemaIdentityLookupStepSpec
  | SourceLookupStepSpec
  | StudiesLookupStepSpec
  | TaskFinderProcessTasksLookupStepSpec
  | TranslationLinksToEntityLookupStepSpec
  | WellCoordinatesLookupStepSpec
  | WellsLookupStepSpec;

// Keep in sync with LookupConfigSpecEditor in benchling/lookup_steps/spec/lookup_step_config_editor.py
export interface LookupConfigSpec {
  isMulti: boolean;
  multiFlattenLevel?: number | null;
  lookupSteps: Array<LookupStepSpec>;
}

export interface LookupStepTreeItemsById {
  [itemId: string]: LookupStepTreeItem;
}

export type LookupStepTreeItem = TreeItem & LookupStepInfo & {depth: number};

export interface LookupStepInfo {
  lookupStep: LookupStepSpec;
  name: string;
  iconToOverrideDefault?: BenchlingIconName;
  shouldSkipValidation?: boolean;
  lookupStepContext?: Array<LookupStepInfo>;
  schemaField?: SchemaField | null;
  collapsingLookupSteps?: Array<CollapsingLookupStepInfo>;
  filterLookupSteps?: Array<FilterLookupStepInfo>;
  modifierLookupSteps?: Array<ModifierLookupStepInfo>;
  hasPlateLayout?: boolean;
  parentSchema: Schema | null;
  linkSchema: Schema | null; // For a typecasted schema interface step, this property coalesces the original interface with the overwritten typecasted schema.
  originalLinkSchema?: Schema | null;
  typecastSchema?: Schema | null;
  // Frontend only additional metadata to be smarter about what following lookups to show when we don't
  // know what specific type a storable lookup is, but know it must be a parent storable
  storableLookupMustBeParent?: boolean;
}

export interface GenericLookupStepInfo<T extends LookupStepSpec> extends LookupStepInfo {
  lookupStep: T;
}

export type CollapsingLookupStepInfo = GenericLookupStepInfo<LatestLookupStepSpec>;

export type FilterLookupStepInfo = GenericLookupStepInfo<FilterLookupStepSpec> & {
  schemaField?: SchemaField;
};

export type FilterByOriginLookupStepInfo = GenericLookupStepInfo<FilterByOriginLookupStepSpec>;

export type ModifierLookupStepInfo = FilterByOriginLookupStepInfo;

export interface SchemaById {
  [schemaId: string]: Schema | undefined;
}

export interface FormErrors {
  rowsToIgnore?: boolean;
  columnsToIgnore?: boolean;
  wellsToIgnore?: boolean;
  dropdownValue?: boolean;
  selectDropdown?: boolean;
  selectTreeError?: boolean;
  missingField?: boolean;
  missingSecondaryField?: boolean;
  filter?: boolean;
  invalidLookupObjectError?: boolean;
}

export interface InjectedLookupStep {
  condition: (lookupObject: Schema) => boolean;
  lookupStep: LookupStepSpec;
  icon: BenchlingIconName;
  name: string;
  linkSchema: Schema | null;
}

const LOOKUP_CONFIG = {
  isMulti: PropTypes.bool,
  lookupSteps: PropTypes.array,
};

const LOOKUP_STEP_SHAPE = PropTypes.oneOfType([
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.ALIASES]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.AUTHORIZED_USERS]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.AVAILABLE_SPACE]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.BARCODE]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.BOX_CONTAINERS]).isRequired,
    order: PropTypes.shape({
      skipRows: PropTypes.number.isRequired,
      skipColumns: PropTypes.number.isRequired,
      fillByQuadrant: PropTypes.bool.isRequired,
      fillDirection: PropTypes.oneOf(['ACROSS_ROWS', 'DOWN_COLUMNS']).isRequired,
    }).isRequired,
    filter: PropTypes.shape({
      ignoreFilled: PropTypes.bool.isRequired,
      ignoreEmpty: PropTypes.bool.isRequired,
    }).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.CHEMICAL_STRUCTURE]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.COMPUTED_EXPIRATION_DATE]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.CONSTANT]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.CONCENTRATION]).isRequired,
    concentrationUnits: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.CONTAINER]).isRequired,
    containerSchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.CONTENTS]).isRequired,
    entitySchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.COUNT]).isRequired,
    schemaField: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.DESTINATION]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.FILTER]).isRequired,
    schemaField: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.FREEZE_THAW_COUNT]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.LOCATION]).isRequired,
    locationSchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.LOCATION_PATH]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.NAME]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.PLACEHOLDER_BOXES]).isRequired,
    boxSchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.PLACEHOLDER_PLATES]).isRequired,
    plateSchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.PLATE]).isRequired,
    plateSchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.REPLICATES]).isRequired,
    numberLookupConfig: PropTypes.shape(LOOKUP_CONFIG).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.RESIDUES]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.REGISTRY_IDENTIFIER]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.RESULTS]).isRequired,
    linkedBySchemaField: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.SAMPLE_OWNERS]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.SCHEMA_FIELD]).isRequired,
    schemaField: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.SCHEMA_IDENTITY]).isRequired,
    entitySchema: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.SOURCE]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.STUDIES]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.QUANTITY]).isRequired,
    volumeUnits: PropTypes.string,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.WELLS]).isRequired,
    order: PropTypes.shape({
      skipRows: PropTypes.number.isRequired,
      skipColumns: PropTypes.number.isRequired,
      fillByQuadrant: PropTypes.bool.isRequired,
      fillDirection: PropTypes.oneOf(['ACROSS_ROWS', 'DOWN_COLUMNS']).isRequired,
    }).isRequired,
    filter: PropTypes.shape({
      ignoreFilled: PropTypes.bool.isRequired,
      ignoreEmpty: PropTypes.bool.isRequired,
    }).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.WELL_COORDINATES]).isRequired,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf([LookupStepType.FILTER_BY_ORIGIN]).isRequired,
  }),
]);

LOOKUP_CONFIG.lookupSteps = PropTypes.arrayOf(LOOKUP_STEP_SHAPE);

export const LOOKUP_CONFIG_SHAPE = PropTypes.shape(LOOKUP_CONFIG);

// Keep in sync with DELETED_SCHEMA_ID in benchling/lookup_steps/spec/lookup_step_spec_editor.py
export const DELETED_SCHEMA_ID = 'DELETED';

export const RIGHT_ARROW = '\u2192';
export const PLUS_SIGN = '\uff0b';

export const BASE_FIRST_STEP_OPTIONS = [
  LookupStepType.SCHEMA_FIELD,
  LookupStepType.COUNT,
  LookupStepType.CONSTANT,
];

export const FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP = [
  LookupStepType.SOURCE,
  LookupStepType.DESTINATION,
  ...BASE_FIRST_STEP_OPTIONS,
];

export const FIRST_STEP_OPTIONS_FOR_DESTINATION = [
  ...BASE_FIRST_STEP_OPTIONS,
  LookupStepType.PLACEHOLDER_BOXES,
  LookupStepType.PLACEHOLDER_PLATES,
];

export const DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES = [
  LookupStepType.BOX,
  LookupStepType.PLATE,
  LookupStepType.CONTENTS,
  LookupStepType.CONTAINER,
  LookupStepType.RESULTS,
  LookupStepType.WELLS,
  LookupStepType.BOX_CONTAINERS,
  LookupStepType.LOCATION,
];

export const DEFAULT_FINAL_LOOKUP_STEP_TYPES = [
  ...DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  LookupStepType.ALIASES,
  LookupStepType.AVAILABLE_SPACE,
  LookupStepType.NAME,
  LookupStepType.SCHEMA_FIELD,
  LookupStepType.COUNT,
  LookupStepType.WELL_COORDINATES,
  LookupStepType.QUANTITY,
  LookupStepType.CONCENTRATION,
  LookupStepType.REGISTRY_IDENTIFIER,
  LookupStepType.RESIDUES,
  LookupStepType.CHEMICAL_STRUCTURE,
  LookupStepType.PLACEHOLDER_BOXES,
  LookupStepType.PLACEHOLDER_PLATES,
  LookupStepType.SOURCE,
  LookupStepType.DESTINATION,
  LookupStepType.AUTHORIZED_USERS,
  LookupStepType.SAMPLE_OWNERS,
  LookupStepType.BARCODE,
  LookupStepType.LOCATION_PATH,
  LookupStepType.COMPUTED_EXPIRATION_DATE,
  LookupStepType.FREEZE_THAW_COUNT,
];

export const NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES = [
  LookupStepType.BOX,
  LookupStepType.PLATE,
  LookupStepType.CHILD_ENTITIES,
  LookupStepType.CONTENTS,
  LookupStepType.CONTAINER,
  LookupStepType.BOX_CONTAINERS,
  LookupStepType.RESULTS,
  LookupStepType.LOCATION,
  LookupStepType.WELLS,
  LookupStepType.TRANSLATION_LINKS_TO_ENTITY,
  LookupStepType.PART_LINKS_TO_ENTITY,
  LookupStepType.AA_PART_LINKS_TO_ENTITY,
];

export const NOTEBOOK_FINAL_LOOKUP_STEP_TYPES = [
  LookupStepType.ALIASES,
  LookupStepType.AVAILABLE_SPACE,
  LookupStepType.NAME,
  LookupStepType.SCHEMA_FIELD,
  LookupStepType.CHILD_ENTITIES,
  LookupStepType.COUNT,
  LookupStepType.WELL_COORDINATES,
  LookupStepType.QUANTITY,
  LookupStepType.CONCENTRATION,
  LookupStepType.REGISTRY_IDENTIFIER,
  LookupStepType.RESIDUES,
  LookupStepType.CHEMICAL_STRUCTURE,
  LookupStepType.BOX,
  LookupStepType.PLATE,
  LookupStepType.CONTENTS,
  LookupStepType.CONTAINER,
  LookupStepType.BOX_CONTAINERS,
  LookupStepType.LOCATION,
  LookupStepType.BARCODE,
  LookupStepType.LOCATION_PATH,
  LookupStepType.WELLS,
  LookupStepType.COMPUTED_EXPIRATION_DATE,
  LookupStepType.FREEZE_THAW_COUNT,
  LookupStepType.STUDIES,
  LookupStepType.TRANSLATION_LINKS_TO_ENTITY,
  LookupStepType.PART_LINKS_TO_ENTITY,
  LookupStepType.AA_PART_LINKS_TO_ENTITY,
];
